import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { setActions } from 'actions/actionRolesSlice';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import Image from 'components/global/Image';
import Loading from 'components/global/Loading';
import PageHeader from 'components/global/PageHeader';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import OtoLinkSettings from 'pages/OTOLink/OTOLinkSettings/OtoLinkSettings';
import { getModuleBySlug, getOTOPackageList } from 'pages/OTOModules/OTOPackageListSlice';
import SubscriptionAccountModal from 'pages/Settings/Payment/SubscriptionAccount/SubscriptionAccountModal';

import { PROFILE } from 'services/consts';

import { setRoles } from 'stores/systemRolesSlice';

import AppLogger from 'utils/AppLogger';
import PATHS from 'utils/paths';

import { faCog, faMinusSquare } from '@fortawesome/pro-light-svg-icons';

import UpgradePlanSuccessModal from '../UpgradePlanSuccessModal';
import DeactivateModule from './DeactivateModule/DeactivateModule';
import * as S from './ModuleDetail.styled';
import ModuleFaqs from './ModuleFaqs';
import ModuleFeature from './ModuleFeature';
import ModuleGuide from './ModuleGuide';
import ModuleHero from './ModuleHero';
import ModuleTestimonials from './ModuleTestimonials';
import RelatedModules from './RelatedModules';

const ModuleDetail = () => {
	const { selectedModule, moduleDetailLoading, otoPackageList } = useSelector((state) => state.otoPackageList);
	const dispatch = useDispatch();
	const params = useParams();
	const user = useSelector((state) => state.user);
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		dispatch(getModuleBySlug(params.slug));
	}, [params.slug]);

	useEffect(() => {
		dispatch(getOTOPackageList());
	}, []);

	const nextPackage = useMemo(() => {
		return otoPackageList.find((item) => item.planName === selectedModule?.currentPackageName);
	}, [otoPackageList, selectedModule]);

	const updateModule = async (active = true) => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(PROFILE.UPDATE_COMPANY_MODULE, {
				moduleId: selectedModule.moduleRoleID
			});

			if (response.status === 200) {
				showSuccess({
					header: t(active ? 'moduleActivated' : 'moduleDeactivated'),
					message: t(active ? 'moduleActivatedMessage' : 'moduleDeactivatedMessage', {
						moduleName: t(selectedModule?.slug)
					})
				});
				const { data: roles } = await axiosPrivate.post(PROFILE.GET_ROLES);
				dispatch(
					setRoles([
						user.planName,
						user.userType,
						...roles.filter((role) => !(role.startsWith('/actions/') || role.startsWith('/rowActions/')))
					])
				);
				dispatch(
					setActions(roles.filter((role) => role.startsWith('/actions/') || role.startsWith('/rowActions/')))
				);
				navigate(`${PATHS.OTO_MODULES}#${selectedModule.packageRoleID}`);
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	};

	const deactivateModule = async () => {
		NiceModal.show(DeactivateModule, {
			updateModule,
			isLoading
		});
	};

	const upgradePlanClickHandler = async () => {
		const result = await NiceModal.show(SubscriptionAccountModal, {
			currentPackage: otoPackageList.find((item) => item.planName === user.planName),
			selectedPackage: nextPackage,
			selectedPlanType: 'MONTHLY'
		});
		if (result.showOTOModuleMessage) {
			setTimeout(async () => {
				const isActivate = await NiceModal.show(UpgradePlanSuccessModal, {
					title: t('activateModules.module.title', { module: t(selectedModule.moduleTitle) }),
					image: selectedModule.moduleIcon
				});
				if (isActivate) updateModule();
			}, 500);
		}
	};

	if (moduleDetailLoading) {
		return <Loading />;
	}
	return (
		<div>
			<PageHeader
				showBackButton
				backUrl={PATHS.OTO_MODULES}
				subtitle="backToModules"
				title={selectedModule?.slug}
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/logs-icon.json.gz"
			/>

			<S.ModuleDetail>
				<S.Header>
					<S.ModuleInfo>
						<Image
							url={selectedModule?.moduleIcon}
							defaultUrl={selectedModule?.moduleIcon}
							alt={selectedModule?.slug}
							width="7rem"
							height="7rem"
						/>
						<S.ModuleTitle>
							<Typography variant="p1" fontWeight="700">
								{selectedModule?.slug}
							</Typography>
							<Badge variant="pill" size="small" color="light" text={selectedModule?.category} />
						</S.ModuleTitle>
					</S.ModuleInfo>

					<Authorization path="/oto-modules/account-owner-actions">
						{selectedModule?.packageRoleID <= user.packageID && !user.accountSuspended && (
							<>
								{selectedModule?.active ? (
									<S.ModuleActions>
										<Button
											id={`${selectedModule?.slug}-deactivateButton`}
											loading={isLoading}
											icon={faMinusSquare}
											label="deactivateModule"
											onClick={deactivateModule}
										/>
										{selectedModule.settingsURL && (
											<Button
												icon={faCog}
												variant="outline"
												label="editSettings"
												onClick={() => {
													selectedModule?.moduleTitle === 'OTOLinkModule'
														? NiceModal.show(OtoLinkSettings)
														: navigate(selectedModule.settingsURL, {
																state: { showBackButton: true, otoModule: true }
															});
												}}
											/>
										)}
									</S.ModuleActions>
								) : (
									<Button
										id={`${selectedModule?.slug}-activateButton`}
										loading={isLoading}
										label="activateModule"
										onClick={updateModule}
									/>
								)}
							</>
						)}
					</Authorization>

					<Authorization path="/oto-modules/account-owner-actions">
						{selectedModule?.packageRoleID > user.packageID &&
							!user.currentPlanExpiryDate &&
							!user.accountSuspended && (
								<Button
									id={`${selectedModule?.slug}-upgradeButton`}
									label={nextPackage?.upgradeButtonLabel}
									disabled={
										user.hasUnpaidSubscription === true &&
										selectedModule?.packageNames?.at(-1) !== 'EnterprisePackage'
									}
									disabledTooltip="renewalFailedButtonDisabledTooltip"
									onClick={upgradePlanClickHandler}
								/>
							)}
					</Authorization>
				</S.Header>
				<S.Content>
					<S.ContentContainer>
						<ModuleHero />
						<ModuleFeature />
						<ModuleGuide />
						<ModuleTestimonials />
						<ModuleFaqs />
						<RelatedModules />
					</S.ContentContainer>
				</S.Content>
			</S.ModuleDetail>
		</div>
	);
};

export default ModuleDetail;
